import React, { Suspense, useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
// Route chunks are lazy-loaded through a wrapper that auto-reloads on a stale
// chunk (after a new deploy) instead of rendering a blank page. See lazyWithReload.
import { lazyWithReload as lazy } from '@/lib/lazyWithReload';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Toaster } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

import { theme, darkTheme } from '@/theme';
import { useUIStore, isLightOnlyPath } from '@/stores/useUIStore';
import { ThemeSync } from '@/components/theme/ThemeControls';
import { FEATURE_FLAGS } from '@/config/features';
import { SWRProvider } from '@/components/providers/SWRProvider';
import { PickersLocalizationProvider } from '@/components/providers/PickersLocalizationProvider';
import { AdminNotificationProvider } from '@/context/AdminNotificationProvider';
import { ConfirmModalProvider } from '@/context/ConfirmModalProvider';
import AiPlanGateModal from '@/newComponents/ai-plan/AiPlanGateModal';
import '@/styles/form.css';

const RTL_LANGUAGES = ['ar'];


// Loading component
const Loading = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
  </div>
);

// The signed-in shell (sidebar, bottom bar, setup wizard, voice stock…) is its own chunk too:
// the login page no longer downloads it before it can paint (QA 19/10 S5-02).
const ProtectedLayout = lazy(() => import('@/layouts/ProtectedLayout'));

// IA & Decisions pages
const CoGerantPage = lazy(() => import('@/app/(protected)/admin/co-gerant/page'));
const CoGerantMemoryPage = lazy(() => import('@/app/(protected)/admin/co-gerant/memoire/page'));
const CoGerantShadowPage = lazy(() => import('@/app/(protected)/admin/co-gerant/shadow/page'));
const CoGerantNotebookPage = lazy(() => import('@/app/(protected)/admin/co-gerant/carnet/page'));
const CoGerantCrisisPage = lazy(() => import('@/app/(protected)/admin/co-gerant/probleme/page'));
const CoGerantWhatsMovingPage = lazy(() => import('@/app/(protected)/admin/co-gerant/ce-qui-bouge/page'));
const CoGerantMyVoicePage = lazy(() => import('@/app/(protected)/admin/co-gerant/ma-voix/page'));
const TodayCountPage = lazy(() => import('@/app/(protected)/admin/inventory/today-count/page'));
const IntelligencePage = lazy(() => import('@/app/(protected)/admin/intelligence/page'));
const PilotagePage = lazy(() => import('@/app/(protected)/admin/pilotage/page'));

// Lazy load pages
const HomePage = lazy(() => import('@/app/page'));

// Admin Auth Pages
const LoginPage = lazy(() => import('@/app/(protected)/admin/(auth)/login/page'));
const GoogleCallbackPage = lazy(() => import('@/app/(protected)/admin/(auth)/google/page'));
const RegisterPage = lazy(() => import('@/app/(protected)/admin/(auth)/register/page'));
const ClaimPage = lazy(() => import('@/app/claim/page'));
const RegisterVerifyPage = lazy(() => import('@/app/(protected)/admin/(auth)/register/verify/[email]/page'));
const ForgetPasswordPage = lazy(() => import('@/app/(protected)/admin/(auth)/forget-password/page'));
const ResetPasswordPage = lazy(() => import('@/app/(protected)/admin/(auth)/reset_password/page'));

// Admin Main Pages
const DashboardPage = lazy(() => import('@/app/(protected)/admin/dashboard/page'));
const DashboardSalesPage = lazy(() => import('@/app/(protected)/admin/dashboard/sales/page'));
// V1 — the dashboard as shipped in production, kept side by side with the redesigned tabs
const DashboardClassicPage = lazy(() => import('@/app/(protected)/admin/dashboard/classic/page'));
const DashboardMenuPage = lazy(() => import('@/app/(protected)/admin/dashboard/menu/page'));
const DashboardTeamPage = lazy(() => import('@/app/(protected)/admin/dashboard/team/page'));
const DashboardCashflowPage = lazy(() => import('@/app/(protected)/admin/dashboard/cashflow/page'));
const DashboardGuestsPage = lazy(() => import('@/app/(protected)/admin/dashboard/guests/page'));
const GettingStartedPage = lazy(() => import('@/app/(protected)/admin/getting-started/page'));
const OrderPage = lazy(() => import('@/app/(protected)/admin/order/page'));
const NotificationsPage = lazy(() => import('@/app/(protected)/admin/notifications/page'));
const KitchenPage = lazy(() => import('@/app/(protected)/admin/kitchen/page'));
const PlaceOrderPage = lazy(() => import('@/app/(protected)/admin/place_order/page'));
const BookingsPage = lazy(() => import('@/app/(protected)/admin/bookings/page'));
const BookingSeatingPage = lazy(() => import('@/app/(protected)/admin/bookings/seating/page'));
const HaccpPage = lazy(() => import('@/app/(protected)/admin/haccp/page'));
const SimulatorPage = lazy(() => import('@/app/(protected)/admin/simulator/page'));
const ChainsPage = lazy(() => import('@/app/(protected)/admin/chains/page'));
const TrainingPage = lazy(() => import('@/app/(protected)/admin/training/page'));
const NotesPage = lazy(() => import('@/app/(protected)/admin/notes/page'));
const OrdersOnlinePage = lazy(() => import('@/app/(protected)/admin/orders-online/page'));
const ActivityLogPage = lazy(() => import('@/app/(protected)/admin/activity-log/page'));
const UploadMenuPage = lazy(() => import('@/app/(protected)/admin/upload_menu/page'));
const AdminSupportPage = lazy(() => import('@/app/(protected)/admin/support/page'));

// Admin Menu Pages
const MenuPage = lazy(() => import('@/app/(protected)/admin/menu/page'));
const MenuAddPage = lazy(() => import('@/app/(protected)/admin/menu/add/page'));
const MenuEditPage = lazy(() => import('@/app/(protected)/admin/menu/edit/page'));

// Admin Category Pages
const CategoryPage = lazy(() => import('@/app/(protected)/admin/category/page'));
const CategoryAddPage = lazy(() => import('@/app/(protected)/admin/category/add/page'));
const CategoryEditPage = lazy(() => import('@/app/(protected)/admin/category/edit/page'));

// Admin Employee Pages
const EmployeePage = lazy(() => import('@/app/(protected)/admin/employee/page'));
const EmployeeAddPage = lazy(() => import('@/app/(protected)/admin/employee/add/page'));
const EmployeeEditPage = lazy(() => import('@/app/(protected)/admin/employee/edit/page'));

// Admin Planning (staffing board — split out of the employee page)
const PlanningPage = lazy(() => import('@/app/(protected)/admin/planning/page'));

// Admin Tables Pages
const TablesPage = lazy(() => import('@/app/(protected)/admin/tables/page'));
const TablesNewPage = lazy(() => import('@/app/(protected)/admin/tables/new/page'));
const TablesEditPage = lazy(() => import('@/app/(protected)/admin/tables/edit/page'));
const TablesSectorsPage = lazy(() => import('@/app/(protected)/admin/tables/sectors/page'));
const TablesPlanPage = lazy(() => import('@/app/(protected)/admin/tables/plan/page'));
// Écran public « commandes prêtes » (TV comptoir) — aucune auth, aucune donnée client
const OrderScreenPage = lazy(() => import('@/app/screen/page'));

// Admin Restaurants Pages
const RestaurantsPage = lazy(() => import('@/app/(protected)/admin/restaurants/page'));
const RestaurantsEditPage = lazy(() => import('@/app/(protected)/admin/restaurants/edit/page'));

// Admin Clients & Campagnes Pages
const ClientsCampagnesPage = lazy(() => import('@/app/(protected)/admin/clients-campagnes/page'));
const ClientsPage = lazy(() => import('@/app/(protected)/admin/clients-campagnes/clients/page'));
const ReglesFidelitePage = lazy(() => import('@/app/(protected)/admin/clients-campagnes/regles-fidelite/page'));
const CampagnesEmailPage = lazy(() => import('@/app/(protected)/admin/clients-campagnes/campagnes-email/page'));

// Admin Marketing Pages
const MarketingPage = lazy(() => import('@/app/(protected)/admin/MarketingPage/page'));
const SocialPage = lazy(() => import('@/app/(protected)/admin/social/page'));
const MarketingAddPage = lazy(() => import('@/app/(protected)/admin/MarketingPage/add/page'));
const MarketingEditPage = lazy(() => import('@/app/(protected)/admin/MarketingPage/edit/page'));

// Admin Costs Pages
const CostsPage = lazy(() => import('@/app/(protected)/admin/costs/page'));
const CostsMarginsPage = lazy(() => import('@/app/(protected)/admin/costs-margins/page'));
const CloseDayPage = lazy(() => import('@/app/(protected)/admin/close-day/page'));
const ProfitLossPage = lazy(() => import('@/app/(protected)/admin/profit-loss/page'));
const MenuAnalysisPage = lazy(() => import('@/app/(protected)/admin/menu-analysis/page'));
const Nf525Page = lazy(() => import('@/app/(protected)/admin/nf525/page'));
const CostsSettingsPage = lazy(() => import('@/app/(protected)/admin/costs/settings/page'));
const DailyCostsPage = lazy(() => import('@/app/(protected)/admin/daily-costs/page'));

// Admin Inventory Pages
const InventoryPage = lazy(() => import('@/app/(protected)/admin/inventory/page'));
const IngredientsPage = lazy(() => import('@/app/(protected)/admin/inventory/ingredients/page'));
const IngredientsNewPage = lazy(() => import('@/app/(protected)/admin/inventory/ingredients/new/page'));
const IngredientsEditPage = lazy(() => import('@/app/(protected)/admin/inventory/ingredients/edit/page'));
const MenuIngredientsPage = lazy(() => import('@/app/(protected)/admin/inventory/menu-ingredients/page'));
const PurchaseOrdersPage = lazy(() => import('@/app/(protected)/admin/inventory/purchase-orders/page'));
const WastePage = lazy(() => import('@/app/(protected)/admin/inventory/waste/page'));
const VisionCountPage = lazy(() => import('@/app/(protected)/admin/inventory/vision-count/page'));
const NegotiatorPage = lazy(() => import('@/app/(protected)/admin/inventory/negotiator/page'));
const MarketPricesPage = lazy(() => import('@/app/(protected)/admin/market-prices/page'));
const StorageZonesPage = lazy(() => import('@/app/(protected)/admin/inventory/storage-zones/page'));
const InvoicesPage = lazy(() => import('@/app/(protected)/admin/inventory/invoices/page'));
const InvoiceDetailPage = lazy(() => import('@/app/(protected)/admin/inventory/invoices/detail/page'));
const SuppliersPage = lazy(() => import('@/app/(protected)/admin/inventory/suppliers/page'));
const CostAlertsPage = lazy(() => import('@/app/(protected)/admin/inventory/cost-alerts/page'));


// Admin Settings Pages
const SettingsPage = lazy(() => import('@/app/(protected)/admin/settings/page'));
const SettingsBrandPage = lazy(() => import('@/app/(protected)/admin/settings/brand/page'));
const SettingsBookingPage = lazy(() => import('@/app/(protected)/admin/settings/booking/page'));
const SettingsHotActionsPage = lazy(() => import('@/app/(protected)/admin/settings/HotActions/page'));
const SettingsPaymentPage = lazy(() => import('@/app/(protected)/admin/settings/payment/page'));
const SettingsPaymentCallbackPage = lazy(() => import('@/app/(protected)/admin/settings/payment/callback/page'));
const OdsFeeRefundsAdminPage = lazy(() => import('@/app/(protected)/admin/ods-fee-refunds-admin/page'));
const SettingsPrintPage = lazy(() => import('@/app/(protected)/admin/settings/print/page'));
const SettingsPrintersPage = lazy(() => import('@/app/(protected)/admin/settings/printers/page'));
const SettingsPrintersAddPage = lazy(() => import('@/app/(protected)/admin/settings/printers/add_printer/page'));
const SettingsPrintersEditPage = lazy(() => import('@/app/(protected)/admin/settings/printers/edit/page'));
const SettingsSecurityPage = lazy(() => import('@/app/(protected)/admin/settings/security/page'));
const SettingsThemeColorsPage = lazy(() => import('@/app/(protected)/admin/settings/theme-colors/page'));
const SettingsUsersPage = lazy(() => import('@/app/(protected)/admin/settings/users/page'));
const SettingsUsersAddPage = lazy(() => import('@/app/(protected)/admin/settings/users/add_user/page'));
const SettingsUsersEditPage = lazy(() => import('@/app/(protected)/admin/settings/users/edit/page'));
const SettingsUsersChangePasswordPage = lazy(() => import('@/app/(protected)/admin/settings/users/change-password/page'));
const SettingsSubscriptionPage = lazy(() => import('@/app/(protected)/admin/settings/subscription/page'));
const SettingsAiTokensPage = lazy(() => import('@/app/(protected)/admin/settings/ai-tokens/page'));
const SettingsIntegrationsPage = lazy(() => import('@/app/(protected)/admin/settings/integrations/page'));

// Support Pages
const SupportLoginPage = lazy(() => import('@/app/(protected)/support/login/page'));
const SupportUserPage = lazy(() => import('@/app/(protected)/support/user/page'));
const SupportUserAddPage = lazy(() => import('@/app/(protected)/support/user/add/page'));
const SupportUserEditPage = lazy(() => import('@/app/(protected)/support/user/edit/page'));
const SupportRestaurantPage = lazy(() => import('@/app/(protected)/support/restaurant/page'));
const SupportRestaurantAddPage = lazy(() => import('@/app/(protected)/support/restaurant/add/page'));
const SupportRestaurantEditPage = lazy(() => import('@/app/(protected)/support/restaurant/edit/page'));
const SupportSubscriptionPage = lazy(() => import('@/app/(protected)/support/subscription/page'));
const SupportCertificatesPage = lazy(() => import('@/app/(protected)/support/certificates/page'));
const SupportTicketsPage = lazy(() => import('@/app/(protected)/support/tickets/page'));
const SupportDetectionPage = lazy(() => import('@/app/(protected)/support/detection/page'));
const SupportDataHubPage = lazy(() => import('@/app/(protected)/support/data-hub/page'));
const SupportJobsPage = lazy(() => import('@/app/(protected)/support/jobs/page'));
const SupportAiCostPage = lazy(() => import('@/app/(protected)/support/ai-cost/page'));
const SupportAiCouponsPage = lazy(() => import('@/app/(protected)/support/ai-coupons/page'));
const SupportAiCouponDetailPage = lazy(() => import('@/app/(protected)/support/ai-coupons/detail/page'));
const SupportAiCouponRestaurantsPage = lazy(() => import('@/app/(protected)/support/ai-coupons/restaurants/page'));
const AdminAiCostPage = lazy(() => import('@/app/(protected)/admin/ai-cost/page'));

function App() {
  // one theme source (src/stores/useUIStore.js) for Tailwind, the CSS tokens and MUI
  const { pathname } = useLocation();
  const isDarkMode = useUIStore((s) => s.isDarkMode) && !isLightOnlyPath(pathname);
  const { i18n } = useTranslation();

  useEffect(() => {
    const dir = RTL_LANGUAGES.includes(i18n.language) ? 'rtl' : 'ltr';
    document.documentElement.setAttribute('dir', dir);
    document.documentElement.setAttribute('lang', i18n.language);
  }, [i18n.language]);

  useEffect(() => {
    const registerSW = async () => {
      try {
        if ('serviceWorker' in navigator) {
          const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');

          registration.onupdatefound = () => {
            const installingWorker = registration.installing;
            if (installingWorker) {
              installingWorker.onstatechange = () => {
                if (installingWorker.state === 'installed') {
                  if (navigator.serviceWorker.controller) {
                    notifyUserAboutUpdate();
                  }
                }
              };
            }
          };
        }
      } catch (error) {
        console.error('SW registration failed:', error);
      }
    };
    registerSW();
  }, []);

  const notifyUserAboutUpdate = () => {
    const updateBanner = document.createElement('div');
    updateBanner.innerHTML = `
      <div style="
        position: fixed;
        bottom: 0;
        width: 100%;
        background: #333;
        color: #fff;
        text-align: center;
        padding: 1em;
        z-index: 1000;
      ">
        ${i18n.t('A new version is available.')} <button id="refresh" style="
          margin-left: 10px;
          padding: 0.5em 1em;
          background: #4CAF50;
          color: white;
          border: none;
          cursor: pointer;
        ">${i18n.t('Refresh')}</button>
      </div>
    `;
    document.body.appendChild(updateBanner);

    document.getElementById('refresh').addEventListener('click', () => {
      window.location.reload();
    });
  };

  return (
    <ThemeProvider theme={isDarkMode ? darkTheme : theme}>
      <CssBaseline />
      <ThemeSync />
      <SWRProvider>
          <PickersLocalizationProvider>
            <AdminNotificationProvider>
              <ConfirmModalProvider>
                        <Suspense fallback={<Loading />}>
                          <Routes>
                            {/* Root */}
                            <Route path="/" element={<HomePage />} />
                            {/* Admin Auth Routes (no layout) */}
                            <Route path="/admin/login" element={<LoginPage />} />
                            <Route path="/admin/auth/google" element={<GoogleCallbackPage />} />
                            <Route path="/admin/register" element={<RegisterPage />} />
                            <Route path="/claim" element={<ClaimPage />} />
                            <Route path="/admin/register/verify/:email" element={<RegisterVerifyPage />} />
                            <Route path="/admin/forget-password" element={<ForgetPasswordPage />} />
                            <Route path="/admin/reset_password" element={<ResetPasswordPage />} />

                            {/* Écran public « commandes prêtes » (TV comptoir) */}
                            <Route path="/screen/:restaurantId" element={<OrderScreenPage />} />

                            {/* Protected Routes with Layout */}
                            <Route element={<ProtectedLayout />}>
                              {/* Dashboard */}
                              <Route path="/admin/dashboard" element={<DashboardPage />} />
                              <Route path="/admin/dashboard/classic" element={<DashboardClassicPage />} />
                              <Route path="/admin/dashboard/sales" element={<DashboardSalesPage />} />
                              <Route path="/admin/dashboard/menu" element={<DashboardMenuPage />} />
                              <Route path="/admin/dashboard/team" element={<DashboardTeamPage />} />
                              <Route path="/admin/dashboard/cashflow" element={<DashboardCashflowPage />} />
                              <Route path="/admin/dashboard/guests" element={<DashboardGuestsPage />} />

                              {/* Getting Started */}
                              <Route path="/admin/getting-started" element={<GettingStartedPage />} />

                              {/* Orders */}
                              <Route path="/admin/order" element={<OrderPage />} />
                              <Route path="/admin/notifications" element={<NotificationsPage />} />
                              <Route path="/admin/kitchen" element={<KitchenPage />} />
                              <Route path="/admin/ods-fee-refunds-admin" element={<OdsFeeRefundsAdminPage />} />
                              <Route path="/admin/place_order" element={<PlaceOrderPage />} />

                              {/* Bookings */}
                              <Route path="/admin/bookings" element={<BookingsPage />} />
                              <Route path="/admin/bookings/seating" element={<BookingSeatingPage />} />
                              <Route path="/admin/haccp" element={<HaccpPage />} />
                              <Route path="/admin/simulator" element={<SimulatorPage />} />
                              <Route path="/admin/chains" element={<ChainsPage />} />
                              <Route path="/admin/training" element={<TrainingPage />} />

                              {/* Notes */}
                              <Route path="/admin/notes" element={<NotesPage />} />

                              {/* « À emporter & Livraison » — one page (owner 16/10); the two old addresses keep working */}
                              <Route path="/admin/orders-online" element={<OrdersOnlinePage />} />
                              <Route path="/admin/click-and-collect" element={<Navigate to="/admin/orders-online?mode=pickup" replace />} />
                              <Route path="/admin/delivery" element={<Navigate to="/admin/orders-online?mode=delivery" replace />} />

                              {/* Activity Log */}
                              <Route path="/admin/activity-log" element={<ActivityLogPage />} />

                              {/* Upload Menu */}
                              <Route path="/admin/upload_menu" element={<UploadMenuPage />} />

                              {/* AI consumption (billed rate; real cost is support-only) */}
                              <Route path="/admin/ai-cost" element={<AdminAiCostPage />} />

                              {/* Support */}
                              <Route path="/admin/support" element={<AdminSupportPage />} />

                              {/* Menu */}
                              <Route path="/admin/menu" element={<MenuPage />} />
                              <Route path="/admin/menu/add" element={<MenuAddPage />} />
                              <Route path="/admin/menu/edit" element={<MenuEditPage />} />

                              {/* Category */}
                              <Route path="/admin/category" element={<CategoryPage />} />
                              <Route path="/admin/category/add" element={<CategoryAddPage />} />
                              <Route path="/admin/category/edit" element={<CategoryEditPage />} />

                              {/* Employee */}
                              <Route path="/admin/employee" element={<EmployeePage />} />
                              <Route path="/admin/employee/add" element={<EmployeeAddPage />} />
                              <Route path="/admin/employee/edit" element={<EmployeeEditPage />} />

                              {/* Planning */}
                              <Route path="/admin/planning" element={<PlanningPage />} />

                              {/* Tables */}
                              <Route path="/admin/tables" element={<TablesPage />} />
                              <Route path="/admin/tables/new" element={<TablesNewPage />} />
                              <Route path="/admin/tables/edit" element={<TablesEditPage />} />
                              <Route path="/admin/tables/sectors" element={<TablesSectorsPage />} />
                              <Route path="/admin/tables/plan" element={<TablesPlanPage />} />

                              {/* Restaurants */}
                              <Route path="/admin/restaurants" element={<RestaurantsPage />} />
                              <Route path="/admin/restaurants/edit" element={<RestaurantsEditPage />} />

                              {/* Clients & Campagnes */}
                              {FEATURE_FLAGS.crm ? (
                                <>
                                  <Route path="/admin/clients-campagnes" element={<ClientsCampagnesPage />} />
                                  <Route path="/admin/clients-campagnes/clients" element={<ClientsPage />} />
                                  <Route path="/admin/clients-campagnes/fidelite" element={<Navigate to="/admin/clients-campagnes/clients" replace />} />
                                  <Route path="/admin/clients-campagnes/regles-fidelite" element={<ReglesFidelitePage />} />
                                  <Route path="/admin/clients-campagnes/campagnes-email" element={<CampagnesEmailPage />} />
                                </>
                              ) : (
                                <>
                                  <Route path="/admin/clients-campagnes" element={<Navigate to="/admin/dashboard" replace />} />
                                  <Route path="/admin/clients-campagnes/*" element={<Navigate to="/admin/dashboard" replace />} />
                                </>
                              )}

                              {/* Marketing */}
                              <Route path="/admin/MarketingPage" element={<MarketingPage />} />
                              <Route path="/admin/MarketingPage/add" element={<MarketingAddPage />} />
                              <Route path="/admin/MarketingPage/edit" element={<MarketingEditPage />} />
                              <Route path="/admin/social" element={<SocialPage />} />

                              {/* Costs */}
                              <Route path="/admin/costs" element={<CostsPage />} />
                              <Route path="/admin/costs-margins" element={<CostsMarginsPage />} />
                              <Route path="/admin/close-day" element={<CloseDayPage />} />
                              <Route path="/admin/profit-loss" element={<ProfitLossPage />} />
                              <Route path="/admin/menu-analysis" element={<MenuAnalysisPage />} />
                              <Route path="/admin/nf525" element={<Nf525Page />} />
                              <Route path="/admin/costs/settings" element={<CostsSettingsPage />} />
                              <Route path="/admin/daily-costs" element={<DailyCostsPage />} />

                              {/* Inventory */}
                              <Route path="/admin/inventory" element={<InventoryPage />} />
                              <Route path="/admin/inventory/ingredients" element={<IngredientsPage />} />
                              <Route path="/admin/inventory/ingredients/new" element={<IngredientsNewPage />} />
                              <Route path="/admin/inventory/ingredients/edit" element={<IngredientsEditPage />} />
                              <Route path="/admin/inventory/menu-ingredients" element={<MenuIngredientsPage />} />
                              {/* the two old AI recipe pages: one recipe assistant now, on the recipe cards */}
                              <Route path="/admin/inventory/menu-ingredients/ai-composition" element={<Navigate to="/admin/inventory/menu-ingredients" replace />} />
                              <Route path="/admin/inventory/menu-ingredients/recipe-scan" element={<Navigate to="/admin/inventory/menu-ingredients" replace />} />
                              <Route path="/admin/inventory/vision-count" element={<VisionCountPage />} />
                              <Route path="/admin/inventory/today-count" element={<TodayCountPage />} />
                              <Route path="/admin/inventory/negotiator" element={<NegotiatorPage />} />
                              <Route path="/admin/market-prices" element={<MarketPricesPage />} />
                              <Route path="/admin/inventory/purchase-orders" element={<PurchaseOrdersPage />} />
                              <Route path="/admin/inventory/waste" element={<WastePage />} />
                              <Route path="/admin/inventory/storage-zones" element={<StorageZonesPage />} />
                              <Route path="/admin/inventory/invoices" element={<InvoicesPage />} />
                              <Route path="/admin/inventory/invoices/detail" element={<InvoiceDetailPage />} />
                              <Route path="/admin/inventory/suppliers" element={<SuppliersPage />} />
                              <Route path="/admin/inventory/cost-alerts" element={<CostAlertsPage />} />

                              {/* IA & Decisions */}
                              <Route path="/admin/co-gerant" element={<CoGerantPage />} />
                              <Route path="/admin/co-gerant/memoire" element={<CoGerantMemoryPage />} />
                              <Route path="/admin/co-gerant/shadow" element={<CoGerantShadowPage />} />
                              <Route path="/admin/co-gerant/carnet" element={<CoGerantNotebookPage />} />
                              <Route path="/admin/co-gerant/probleme" element={<CoGerantCrisisPage />} />
                              <Route path="/admin/co-gerant/ce-qui-bouge" element={<CoGerantWhatsMovingPage />} />
                              <Route path="/admin/co-gerant/ma-voix" element={<CoGerantMyVoicePage />} />
                              <Route path="/admin/intelligence" element={<IntelligencePage />} />
                              <Route path="/admin/pilotage" element={<PilotagePage />} />

                              {/* Settings */}
                              <Route path="/admin/settings" element={<SettingsPage />} />
                              <Route path="/admin/settings/brand" element={<SettingsBrandPage />} />
                              <Route path="/admin/settings/booking" element={<SettingsBookingPage />} />
                              {/* the Click & Collect link and QR code live on « À emporter & Livraison › Mon lien » (16/10) */}
                              <Route path="/admin/settings/clickcollect" element={<Navigate to="/admin/orders-online?tab=link" replace />} />
                              <Route path="/admin/settings/HotActions" element={<SettingsHotActionsPage />} />
                              {/* The old hot-action-only list: the unified notification center replaces it */}
                              <Route path="/admin/settings/Notifications" element={<Navigate to="/admin/notifications" replace />} />
                              <Route path="/admin/settings/payment" element={<SettingsPaymentPage />} />
                              <Route path="/admin/settings/payment/callback" element={<SettingsPaymentCallbackPage />} />
                              <Route path="/admin/settings/print" element={<SettingsPrintPage />} />
                              <Route path="/admin/settings/printers" element={<SettingsPrintersPage />} />
                              <Route path="/admin/settings/printers/add_printer" element={<SettingsPrintersAddPage />} />
                              <Route path="/admin/settings/printers/edit" element={<SettingsPrintersEditPage />} />
                              <Route path="/admin/settings/security" element={<SettingsSecurityPage />} />
                              <Route path="/admin/settings/theme-colors" element={<SettingsThemeColorsPage />} />
                              <Route path="/admin/settings/users" element={<SettingsUsersPage />} />
                              <Route path="/admin/settings/users/add_user" element={<SettingsUsersAddPage />} />
                              <Route path="/admin/settings/users/edit" element={<SettingsUsersEditPage />} />
                              <Route path="/admin/settings/users/change-password" element={<SettingsUsersChangePasswordPage />} />
                              <Route path="/admin/settings/subscription" element={<SettingsSubscriptionPage />} />
                              <Route path="/admin/settings/ai-tokens" element={<SettingsAiTokensPage />} />
                              <Route path="/admin/settings/integrations" element={<SettingsIntegrationsPage />} />

                              {/* Support */}
                              <Route path="/support" element={<SupportTicketsPage />} />
                              <Route path="/support/login" element={<SupportLoginPage />} />
                              <Route path="/support/user" element={<SupportUserPage />} />
                              <Route path="/support/user/add" element={<SupportUserAddPage />} />
                              <Route path="/support/user/edit" element={<SupportUserEditPage />} />
                              <Route path="/support/restaurant" element={<SupportRestaurantPage />} />
                              <Route path="/support/restaurant/add" element={<SupportRestaurantAddPage />} />
                              <Route path="/support/restaurant/edit" element={<SupportRestaurantEditPage />} />
                              <Route path="/support/subscription" element={<SupportSubscriptionPage />} />
                              <Route path="/support/certificates" element={<SupportCertificatesPage />} />
                              <Route path="/support/detection" element={<SupportDetectionPage />} />
                              <Route path="/support/data-hub" element={<SupportDataHubPage />} />
                              <Route path="/support/jobs" element={<SupportJobsPage />} />
                              <Route path="/support/ai-cost" element={<SupportAiCostPage />} />
                              <Route path="/support/ai-coupons" element={<SupportAiCouponsPage />} />
                              <Route path="/support/ai-coupons/detail" element={<SupportAiCouponDetailPage />} />
                              <Route path="/support/ai-coupons/restaurants" element={<SupportAiCouponRestaurantsPage />} />
                              <Route path="/support/tickets" element={<Navigate to="/support" replace />} />
                            </Route>

                            {/* Catch all - redirect to home */}
                            <Route path="*" element={<Navigate to="/" replace />} />
                          </Routes>
                        </Suspense>
                        {/* « Cette fonction est incluse dans la formule … » — opened by the API client */}
                        <AiPlanGateModal />
                        <Toaster
                          position="top-right"
                          toastOptions={{
                            duration: 5000,
                            style: {
                              background: '#363636',
                              color: '#fff',
                            },
                            success: {
                              style: {
                                background: '#4caf50',
                              },
                            },
                            error: {
                              style: {
                                background: '#f44336',
                              },
                            },
                          }}
                        />
              </ConfirmModalProvider>
            </AdminNotificationProvider>
          </PickersLocalizationProvider>
      </SWRProvider>
    </ThemeProvider>
  );
}

export default App;
